import { AsyncPipe, CommonModule, ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CalloutComponent } from '@rma/generic/ui/callout';
import { RmaIconComponent } from '@rma/generic/ui/icon';
import { SkeletonComponent } from '@rma/generic/ui/skeleton';
import { DeviceSizeService, RmaBreakpoint } from '@rma/generic/util/device-size';
import { BrandedHeaderComponent } from '@rma/profiles/public/ui-branded-header';
import { Observable, combineLatest, map } from 'rxjs';
import type { FindMyAgentResponseVM } from '../domain/results';
import { FindMyAgentStore, View } from '../orc-find-my-agent/find-my-agent.store';
import { ResultsContactFormComponent } from './ui-contact-form/contact-form.component';
import { ResultsFooterComponent } from './ui-footer/footer.component';
import { ResultsPagingComponent } from './ui-paging/paging.component';
import { ResultProfileComponent } from './ui-profile/profile.component';
import { ResultTableComponent } from './ui-result-table/result-table.component';
import { ResultComponent } from './ui-result/result.component';

@UntilDestroy()
@Component({
  selector: 'rma-results-view-toggle',
  imports: [
    AsyncPipe,
    BrandedHeaderComponent,
    CalloutComponent,
    CommonModule,
    MatButtonModule,
    MatButtonToggleModule,
    ResultComponent,
    ResultProfileComponent,
    ResultTableComponent,
    ResultsContactFormComponent,
    ResultsFooterComponent,
    ResultsPagingComponent,
    RmaIconComponent,
    RouterModule,
    SkeletonComponent,
    TranslocoModule,
  ],
  templateUrl: './results.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block mx-auto px-2 mt-6 md:mt-0 md:p-0 md:bg-blue-10 min-h-screen',
  },
})
export class ResultsComponent implements OnInit {
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly deviceSizeService = inject(DeviceSizeService);

  protected readonly store = inject(FindMyAgentStore);
  protected readonly activatedRoute = inject(ActivatedRoute);

  protected readonly contactFormId = 'contact-form';

  public showHeader$: Observable<boolean>;

  constructor() {
    this.showHeader$ = combineLatest({
      isLargeAndAbove: this.deviceSizeService.observe(RmaBreakpoint['>LG']),
      view: this.store.view$,
    }).pipe(map(({ isLargeAndAbove, view }) => isLargeAndAbove || view === 'block'));
  }

  ngOnInit(): void {
    this.store.getResultsEffect(this.store.criteria$);
  }

  public onContact(profileCode: string): void {
    this.store.patchState({ contact: profileCode, contactSubmitting: null, contactError: undefined });
    setTimeout(() => {
      this.viewportScroller.scrollToAnchor(this.contactFormId);
    }, 100);
  }

  agentsTrackBy(_index: number, agent: FindMyAgentResponseVM): string {
    return agent.profileCode;
  }

  onAgentSelectedFromTableView(agentCode: string) {
    this.store.setView('block');
    setTimeout(() => this.viewportScroller.scrollToAnchor(agentCode), 100);
  }

  onToggleView(current: View) {
    this.store.setView(current === 'block' ? 'table' : 'block');
  }
}
